import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Printer, X } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button } from '@/components/ui/button';
import { pcmpProposalDetailQueryOptions } from '../api/pcmpProposals.api';
import { PcmpProposalReport } from '../components/PcmpProposalReport/PcmpProposalReport';

// The sheet is exactly one A4 page; the browser adds no margin of its own.
const PAGE_STYLE = '@page { size: A4 portrait; margin: 0; }';

/**
 * Scales the report down until it fits the sheet (legacy fitToPage), so a
 * long student table never spills onto a second page, then opens the print
 * dialog once.
 */
function useFitToSheetAndPrint(isReady: boolean) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isReady) return;
    const sheet = sheetRef.current;
    const content = contentRef.current;
    if (!sheet || !content) return;

    const fit = () => {
      const style = window.getComputedStyle(sheet);
      const available =
        sheet.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      content.style.transform = 'none';
      content.style.width = '100%';
      const height = content.scrollHeight;
      if (height > available) {
        const scale = available / height;
        content.style.width = `${100 / scale}%`;
        content.style.transform = `scale(${scale})`;
      }
    };

    fit();
    // Web fonts can land after the first layout and change the height.
    void document.fonts.ready.then(fit);
    const timer = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(timer);
  }, [isReady]);

  return { sheetRef, contentRef };
}

export function PcmpProposalPrintPage() {
  const { proposalId } = useParams({ from: '/_print/pcmp/proposals/$proposalId/print' });
  const [printedAt] = useState(() => new Date());
  const {
    data: proposal,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    ...pcmpProposalDetailQueryOptions(proposalId),
    // Closing the print dialog refocuses the tab; keep the sheet as printed.
    refetchOnWindowFocus: false,
  });
  const { sheetRef, contentRef } = useFitToSheetAndPrint(proposal !== undefined);

  useEffect(() => {
    if (proposal) document.title = `PCMP Proposal ${proposal.refNo}`;
  }, [proposal]);

  if (isLoading) return <PageLoader />;

  if (error || !proposal) {
    return (
      <div className="p-6">
        <ErrorState
          error={error}
          fallbackTitle="Could not load this proposal"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  return (
    <div className="min-h-svh bg-neutral-200 py-6 print:bg-white print:py-0">
      <style>{PAGE_STYLE}</style>
      <div className="mx-auto mb-4 flex w-[210mm] max-w-full justify-end gap-2 px-4 print:hidden">
        <Button type="button" onClick={() => window.print()} className="gap-1.5">
          <Printer className="size-4" />
          Print / Save as PDF
        </Button>
        <Button type="button" variant="outline" onClick={() => window.close()} className="gap-1.5">
          <X className="size-4" />
          Close
        </Button>
      </div>
      <div className="overflow-x-auto print:overflow-visible">
        <div
          ref={sheetRef}
          className="mx-auto h-[297mm] w-[210mm] overflow-hidden bg-white p-[10mm] shadow-lg print:shadow-none"
        >
          <div ref={contentRef} className="origin-top-left">
            <PcmpProposalReport proposal={proposal} printedAt={printedAt} />
          </div>
        </div>
      </div>
    </div>
  );
}
